// ============================================
// SYSCOMADMIN.JS — Panel de salud de la conexión con Syscom
// ============================================

import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  API_BASE_CLI, PP, COLOR_OSCURO, COLOR_FONDO, COLOR_GRIS,
  COLOR_GRIS_CLARO, COLOR_BORDE,
  estiloInput, estiloBotonPrimario, estiloBotonSecundario, estiloTarjeta,
} from './components/helpersClientes';
import SelectorMoneda from './components/SelectorMoneda';
import AvisoSincronizacionSyscom from './components/AvisoSincronizacionSyscom';
import { useUsuario } from './context/UsuarioContext';
// Header de autorizacion para los endpoints protegidos de import. Usa EXACTAMENTE
// el mismo mecanismo que las pantallas autenticadas que ya funcionan (Usuarios.js):
// leer 'beep_token' crudo de localStorage. Asi el header Authorization es byte a
// byte identico al de los endpoints autenticados ya probados.
function importAuthHeaders() {
  const t = localStorage.getItem('beep_token');
  return t ? { Authorization: `Bearer ${t}` } : {};
}

const VERDE = '#10b981';
const AMARILLO = '#f59e0b';
const ROJO = '#ef4444';

function fmtFecha(iso) {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    return d.toLocaleString('es-MX', {
      day: '2-digit', month: 'short', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  } catch { return '—'; }
}

function hace(iso) {
  if (!iso) return null;
  const diff = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (diff < 60) return `hace ${diff}s`;
  if (diff < 3600) return `hace ${Math.floor(diff / 60)} min`;
  if (diff < 86400) return `hace ${Math.floor(diff / 3600)} h`;
  return `hace ${Math.floor(diff / 86400)} d`;
}

export default function SyscomAdmin() {
  const [status, setStatus] = useState(null);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState(null);
  const [probando, setProbando] = useState(false);
  const [resultadoTest, setResultadoTest] = useState(null);
  const [limpiando, setLimpiando] = useState(false);
  const [toast, setToast] = useState(null);

  const [categoriasDisponibles, setCategoriasDisponibles] = useState(null);
  const [marcasDisponibles, setMarcasDisponibles] = useState(null);

  const cargar = useCallback(async () => {
    setError(null);
    try {
      const r = await fetch(`${API_BASE_CLI}/api/syscom/status`).then(x => x.json());
      if (r.ok) {
        setStatus(r);
      } else {
        setError(r.error || 'No se pudo leer el status');
      }
    } catch (e) {
      setError(e.message);
    } finally {
      setCargando(false);
    }
  }, []);

  const cargarDatos = useCallback(async () => {
    try {
      const [cats, marcas] = await Promise.all([
        fetch(`${API_BASE_CLI}/api/syscom/categorias`).then(x => x.json()).catch(() => null),
        fetch(`${API_BASE_CLI}/api/syscom/marcas`).then(x => x.json()).catch(() => null),
      ]);
      if (cats && cats.ok) {
        const lista = Array.isArray(cats.categorias) ? cats.categorias
          : (cats.categorias && cats.categorias.data ? cats.categorias.data : []);
        setCategoriasDisponibles(Array.isArray(lista) ? lista.length : null);
      }
      if (marcas && marcas.ok) {
        const lista = Array.isArray(marcas.marcas) ? marcas.marcas
          : (marcas.marcas && marcas.marcas.data ? marcas.marcas.data : []);
        setMarcasDisponibles(Array.isArray(lista) ? lista.length : null);
      }
    } catch {}
  }, []);

  useEffect(() => {
    cargar();
    cargarDatos();
    const id = setInterval(cargar, 30000);
    return () => clearInterval(id);
  }, [cargar, cargarDatos]);

  const probar = async () => {
    setProbando(true);
    setResultadoTest(null);
    try {
      const r = await fetch(`${API_BASE_CLI}/api/syscom/test`, { method: 'POST', headers: { ...importAuthHeaders() } }).then(x => x.json());
      setResultadoTest(r);
      if (r.ok) {
        setToast('✅ Conexión a Syscom OK');
      } else {
        setToast('⚠️ ' + (r.mensaje || r.error || 'Falló la prueba'));
      }
      await cargar();
    } catch (e) {
      setToast('❌ ' + e.message);
    } finally {
      setProbando(false);
      setTimeout(() => setToast(null), 4000);
    }
  };

  const limpiarCache = async () => {
    if (!window.confirm('Esto borra todas las entradas del cache de Syscom. ¿Continuar?')) return;
    setLimpiando(true);
    try {
      const r = await fetch(`${API_BASE_CLI}/api/syscom/cache/clear`, { method: 'POST', headers: { ...importAuthHeaders() } }).then(x => x.json());
      if (r.ok) {
        setToast(`✅ Cache limpiado (${r.entradas_borradas} entrada${r.entradas_borradas === 1 ? '' : 's'})`);
      } else {
        setToast('⚠️ ' + (r.error || 'error'));
      }
    } catch (e) {
      setToast('❌ ' + e.message);
    } finally {
      setLimpiando(false);
      setTimeout(() => setToast(null), 4000);
    }
  };

  const estadoGlobal = useEstado(status, error);

  return (
    <div style={{ fontFamily: PP, color: COLOR_GRIS, padding: 20, background: COLOR_FONDO, minHeight: '100%' }}>
      <div style={{
        marginBottom: 14, display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between',
        gap: 12, flexWrap: 'wrap',
      }}>
        <div style={{ flex: 1, minWidth: 260 }}>
          <div style={{ fontSize: 20, fontWeight: 700, color: COLOR_OSCURO }}>
            🔌 Conexión con Syscom
          </div>
          <div style={{ fontSize: 12, color: COLOR_GRIS_CLARO, marginTop: 4 }}>
            Estado del conector OAuth, token vigente, peticiones, rate limit y cache.
          </div>
        </div>
        <SelectorMoneda />
      </div>

      {/* W1.1-B: la tarjeta de TC se movio a su propia pestana "Tipo de cambio" (aplica a todos los productos). */}

      {/* SYSCOM-REIMPORT (2026-09-21): lo PRIMERO de la pantalla es si el catalogo esta
          al dia. Antes habia que bajar hasta el panel de importacion y saber interpretar
          un "❌" para enterarse de que el barrido llevaba 24 dias muerto. */}
      <AvisoSincronizacionSyscom estilo={{ marginBottom: 14 }} />

      {cargando ? (
        <div style={{ ...estiloTarjeta, padding: 40, textAlign: 'center', color: COLOR_GRIS_CLARO }}>
          Cargando estado…
        </div>
      ) : (
        <>
          <IndicadorGlobal estado={estadoGlobal} status={status} error={error} />

          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(230px, 1fr))',
            gap: 12, marginTop: 16,
          }}>
            <Card
              color={status?.token_vigente ? VERDE : ROJO}
              titulo="Token vigente"
              valor={status?.token_vigente ? '✅ Sí' : '❌ No'}
              detalle={status?.token_expira_en ? `Expira: ${fmtFecha(status.token_expira_en)}` : 'Sin token válido'}
            />
            <Card
              color={status?.ultima_peticion_exitosa ? VERDE : AMARILLO}
              titulo="Última petición"
              valor={status?.ultima_peticion_exitosa ? hace(status.ultima_peticion_exitosa) : 'Sin datos'}
              detalle={status?.ultima_peticion_exitosa ? fmtFecha(status.ultima_peticion_exitosa) : '—'}
            />
            <Card
              color={status?.peticiones_ultima_hora > 0 ? VERDE : COLOR_GRIS_CLARO}
              titulo="Peticiones (1h)"
              valor={String(status?.peticiones_ultima_hora ?? 0)}
              detalle="Total de llamadas reales a Syscom"
            />
            <Card
              color={status?.errores_ultima_hora > 0 ? ROJO : VERDE}
              titulo="Errores (1h)"
              valor={String(status?.errores_ultima_hora ?? 0)}
              detalle={status?.errores_ultima_hora > 0 ? 'Revisa logs del servidor' : 'Todo bien'}
            />
          </div>

          <div style={{ ...estiloTarjeta, marginTop: 12, padding: 16 }}>
            <div style={{ fontSize: 12, color: COLOR_GRIS_CLARO, marginBottom: 6, textTransform: 'uppercase', letterSpacing: 0.3 }}>
              Rate limit (Syscom permite 60/min)
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
              <div style={{ fontSize: 18, fontWeight: 700, color: COLOR_OSCURO }}>
                {status?.peticiones_ultimo_minuto ?? 0} / 60
              </div>
              <div style={{
                flex: 1, minWidth: 160, height: 8, background: COLOR_FONDO,
                borderRadius: 99, overflow: 'hidden', border: `1px solid ${COLOR_BORDE}`,
              }}>
                <div style={{
                  width: `${Math.min(100, ((status?.peticiones_ultimo_minuto || 0) / 60) * 100)}%`,
                  height: '100%',
                  background: (status?.peticiones_ultimo_minuto || 0) >= 55 ? ROJO : (status?.peticiones_ultimo_minuto || 0) >= 40 ? AMARILLO : VERDE,
                  transition: 'width 0.3s',
                }} />
              </div>
              <div style={{ fontSize: 12, color: status?.rate_limit_ok ? VERDE : ROJO, fontWeight: 600 }}>
                {status?.rate_limit_ok ? '✅ OK' : '⚠️ Riesgo'}
              </div>
            </div>
          </div>

          <div style={{ ...estiloTarjeta, marginTop: 12, padding: 16 }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: COLOR_OSCURO, marginBottom: 10 }}>
              Credenciales
            </div>
            <div style={{ fontSize: 12, color: COLOR_GRIS }}>
              {status?.credenciales_configuradas
                ? '✅ SYSCOM_CLIENT_ID y SYSCOM_CLIENT_SECRET configuradas'
                : '❌ Faltan SYSCOM_CLIENT_ID / SYSCOM_CLIENT_SECRET en el .env del backend'}
            </div>
          </div>

          {/* SYSCOM-MARGEN: margen de utilidad global — visible/editable solo para direccion */}
          <TarjetaMargenUtilidad />

          <div style={{ display: 'flex', gap: 10, marginTop: 16, flexWrap: 'wrap' }}>
            <button
              style={{ ...estiloBotonPrimario, opacity: probando ? 0.6 : 1 }}
              disabled={probando}
              onClick={probar}
            >
              {probando ? '⏳ Probando…' : '🧪 Probar conexión'}
            </button>
            <button
              style={{ ...estiloBotonSecundario, opacity: limpiando ? 0.6 : 1 }}
              disabled={limpiando}
              onClick={limpiarCache}
            >
              {limpiando ? '⏳ Limpiando…' : '🔄 Limpiar cache'}
            </button>
            <button
              style={estiloBotonSecundario}
              onClick={() => { cargar(); cargarDatos(); }}
            >
              ↻ Refrescar
            </button>
          </div>

          {resultadoTest && (
            <div style={{
              ...estiloTarjeta, marginTop: 14, padding: 16,
              borderLeft: `4px solid ${resultadoTest.ok ? VERDE : ROJO}`,
            }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: resultadoTest.ok ? VERDE : ROJO }}>
                {resultadoTest.ok ? '✅ Prueba exitosa' : '❌ Prueba falló'}
              </div>
              <div style={{ fontSize: 12, marginTop: 6 }}>
                {resultadoTest.mensaje || resultadoTest.error || '—'}
              </div>
              {resultadoTest.categorias_count != null && (
                <div style={{ fontSize: 11, color: COLOR_GRIS_CLARO, marginTop: 4 }}>
                  Categorías obtenidas: <strong>{resultadoTest.categorias_count}</strong>
                </div>
              )}
            </div>
          )}

          <div style={{ ...estiloTarjeta, marginTop: 16, padding: 16 }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: COLOR_OSCURO, marginBottom: 10 }}>
              📊 Datos disponibles
            </div>
            <div style={{ display: 'grid', gap: 6, fontSize: 13 }}>
              <div>
                · {categoriasDisponibles != null ? <strong>{categoriasDisponibles}</strong> : '—'} categorías
              </div>
              <div>
                · {marcasDisponibles != null ? <strong>{marcasDisponibles}</strong> : '—'} marcas
              </div>
            </div>
            <div style={{ fontSize: 11, color: COLOR_GRIS_CLARO, marginTop: 6 }}>
              Respuesta cacheada 24h — se refresca automáticamente al expirar.
            </div>
          </div>

          <PanelSyncCategorias />
          <PanelActualizarCatalogo />
          <PanelImportarCatalogo />
        </>
      )}

      {error && !cargando && (
        <div style={{
          ...estiloTarjeta, marginTop: 16, padding: 16,
          borderLeft: `4px solid ${ROJO}`, color: COLOR_GRIS,
        }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: ROJO, marginBottom: 4 }}>
            ❌ No se pudo consultar el status
          </div>
          <div style={{ fontSize: 12 }}>{error}</div>
        </div>
      )}

      {toast && (
        <div style={{
          position: 'fixed', bottom: 20, left: '50%', transform: 'translateX(-50%)',
          background: '#1a3a5c', color: '#fff', padding: '10px 16px', borderRadius: 10,
          fontSize: 12, zIndex: 1500, fontFamily: PP, maxWidth: '90%',
        }}>{toast}</div>
      )}
    </div>
  );
}

// ============================================
// SUBCOMPONENTES
// ============================================

// SYSCOM-MARGEN — Margen de utilidad global Syscom (margen real sobre precio).
// Solo direccion (accesoTotal) lo ve y lo edita. Deny-by-default: si no es
// direccion, la tarjeta no se renderiza. Lee/escribe via /api/cotizador/config.
function TarjetaMargenUtilidad() {
  const { accesoTotal } = useUsuario();
  const [valor, setValor] = useState('');
  const [original, setOriginal] = useState('');
  const [cargando, setCargando] = useState(true);
  const [guardando, setGuardando] = useState(false);
  const [toast, setToast] = useState(null);

  const cargar = useCallback(async () => {
    setCargando(true);
    try {
      const r = await fetch(`${API_BASE_CLI}/api/cotizador/config`).then(x => x.json());
      if (r.ok) {
        const v = r.config?.margen_utilidad_global;
        const s = v != null ? String(v) : '25';
        setValor(s);
        setOriginal(s);
      }
    } catch {
      // silencioso: si falla, el input queda vacio y el boton deshabilitado
    } finally {
      setCargando(false);
    }
  }, []);

  useEffect(() => {
    if (accesoTotal) cargar();
  }, [accesoTotal, cargar]);

  // Deny-by-default: solo direccion. Si no, no se muestra nada.
  if (!accesoTotal) return null;

  const sucio = valor !== original;

  const guardar = async () => {
    const n = Number(valor);
    if (!Number.isFinite(n) || n < 0 || n > 95) {
      setToast('⚠️ El margen debe ser un numero entre 0 y 95');
      setTimeout(() => setToast(null), 3000);
      return;
    }
    setGuardando(true);
    try {
      const r = await fetch(`${API_BASE_CLI}/api/cotizador/config`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', ...importAuthHeaders() },
        body: JSON.stringify({ margen_utilidad_global: n }),
      }).then(x => x.json());
      if (!r.ok) throw new Error(r.error || 'error');
      setToast('✅ Margen actualizado — la tienda Syscom ya se reprecio');
      await cargar(); // refresca para que se note el cambio
    } catch (e) {
      setToast('⚠️ ' + e.message);
    } finally {
      setGuardando(false);
      setTimeout(() => setToast(null), 3500);
    }
  };

  return (
    <div style={{ ...estiloTarjeta, marginTop: 12, padding: 16, borderLeft: `4px solid ${VERDE}` }}>
      <div style={{ fontSize: 13, fontWeight: 700, color: COLOR_OSCURO, marginBottom: 6 }}>
        💵 Margen de utilidad
      </div>
      <div style={{ fontSize: 11, color: COLOR_GRIS_CLARO, marginBottom: 12, lineHeight: 1.5 }}>
        Es el margen real que ganas sobre el precio. Cambiarlo repreciea toda la tienda Syscom al instante.
      </div>
      {cargando ? (
        <div style={{ fontSize: 12, color: COLOR_GRIS_CLARO }}>Cargando…</div>
      ) : (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <div style={{ position: 'relative', width: 140 }}>
            <input
              style={{ ...estiloInput, paddingRight: 30, textAlign: 'right', fontSize: 15, fontWeight: 700 }}
              type="number" step="0.1" min="0" max="95"
              value={valor}
              onChange={(e) => setValor(e.target.value)}
            />
            <span style={{
              position: 'absolute', right: 10, top: 9,
              color: COLOR_GRIS_CLARO, fontSize: 13,
            }}>%</span>
          </div>
          <button
            style={{ ...estiloBotonPrimario, opacity: !sucio || guardando ? 0.5 : 1 }}
            disabled={!sucio || guardando}
            onClick={guardar}
          >
            {guardando ? 'Guardando…' : '💾 Guardar margen'}
          </button>
        </div>
      )}
      {toast && (
        <div style={{ marginTop: 10, fontSize: 12, color: COLOR_GRIS }}>{toast}</div>
      )}
    </div>
  );
}

function useEstado(status, error) {
  if (error) return 'error';
  if (!status) return 'desconocido';
  if (!status.credenciales_configuradas) return 'error';
  if (!status.token_vigente) return 'warning';
  if (status.errores_ultima_hora > 0) return 'warning';
  return 'ok';
}

function IndicadorGlobal({ estado, status, error }) {
  const cfg = {
    ok:          { color: VERDE,    label: 'Conectado',  icono: '🟢' },
    warning:     { color: AMARILLO, label: 'Con avisos', icono: '🟡' },
    error:       { color: ROJO,     label: 'Desconectado', icono: '🔴' },
    desconocido: { color: COLOR_GRIS_CLARO, label: 'Sin datos', icono: '⚪' },
  }[estado] || { color: COLOR_GRIS_CLARO, label: '—', icono: '⚪' };

  return (
    <div style={{
      ...estiloTarjeta, padding: 18,
      borderLeft: `6px solid ${cfg.color}`,
      display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap',
    }}>
      <div style={{
        fontSize: 40,
        animation: estado === 'ok' ? 'pulse 2s infinite' : 'none',
      }}>
        {cfg.icono}
      </div>
      <div style={{ flex: 1, minWidth: 200 }}>
        <div style={{ fontSize: 11, color: COLOR_GRIS_CLARO, textTransform: 'uppercase', letterSpacing: 0.3 }}>
          Estado
        </div>
        <div style={{ fontSize: 20, fontWeight: 700, color: cfg.color }}>
          {cfg.label}
        </div>
        <div style={{ fontSize: 12, color: COLOR_GRIS, marginTop: 4 }}>
          {estado === 'ok' && 'Todo funcionando con normalidad.'}
          {estado === 'warning' && !status?.token_vigente && 'No hay token vigente — al primer request se renovará.'}
          {estado === 'warning' && status?.token_vigente && status?.errores_ultima_hora > 0 && `${status.errores_ultima_hora} error(es) en la última hora.`}
          {estado === 'error' && !status?.credenciales_configuradas && 'Faltan credenciales en el backend.'}
          {estado === 'error' && status?.credenciales_configuradas && (error || 'Error leyendo el status.')}
          {estado === 'desconocido' && 'Aún no tengo información de Syscom.'}
        </div>
      </div>
      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50%      { opacity: 0.55; }
        }
      `}</style>
    </div>
  );
}

function Card({ color, titulo, valor, detalle }) {
  return (
    <div style={{
      ...estiloTarjeta, padding: 14,
      borderLeft: `4px solid ${color}`,
    }}>
      <div style={{ fontSize: 10, color: COLOR_GRIS_CLARO, textTransform: 'uppercase', letterSpacing: 0.3 }}>
        {titulo}
      </div>
      <div style={{ fontSize: 18, fontWeight: 700, color: COLOR_OSCURO, marginTop: 4 }}>
        {valor}
      </div>
      {detalle && (
        <div style={{ fontSize: 11, color: COLOR_GRIS_CLARO, marginTop: 4 }}>
          {detalle}
        </div>
      )}
    </div>
  );
}

// ============================================
// PanelSyncCategorias — botón para sincronizar el árbol Syscom (3 niveles)
// ============================================
function PanelSyncCategorias() {
  const [estado, setEstado] = useState(null);
  const [arbolInfo, setArbolInfo] = useState(null);
  const [iniciando, setIniciando] = useState(false);
  const [mensaje, setMensaje] = useState(null);
  const intervalRef = useRef(null);

  const cargarEstado = useCallback(async () => {
    try {
      const r = await fetch(`${API_BASE_CLI}/api/syscom/sync-categorias/estado`).then(x => x.json());
      if (r.ok) setEstado(r.estado);
    } catch {}
  }, []);

  const cargarArbol = useCallback(async () => {
    try {
      const r = await fetch(`${API_BASE_CLI}/api/syscom/categorias-arbol`).then(x => x.json());
      if (r.ok) setArbolInfo({ total: r.total || 0, raices: (r.arbol || []).length });
    } catch {}
  }, []);

  useEffect(() => {
    cargarEstado();
    cargarArbol();
  }, [cargarEstado, cargarArbol]);

  // Mientras corre, refresca cada 3s
  useEffect(() => {
    if (estado?.corriendo) {
      if (intervalRef.current) return;
      intervalRef.current = setInterval(() => {
        cargarEstado();
      }, 3000);
    } else {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
        cargarArbol(); // refrescar contadores al terminar
      }
    }
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [estado?.corriendo, cargarEstado, cargarArbol]);

  const iniciarSync = async () => {
    if (!window.confirm('Esto consultará Syscom recursivamente y puede tardar 2-5 minutos. ¿Continuar?')) return;
    setIniciando(true);
    setMensaje(null);
    try {
      const r = await fetch(`${API_BASE_CLI}/api/syscom/sync-categorias`, { method: 'POST', headers: { ...importAuthHeaders() } }).then(x => x.json());
      if (r.ok) {
        setMensaje('✅ Sync iniciado en background — verás el progreso debajo.');
        setEstado(r.estado);
      } else {
        setMensaje('⚠️ ' + (r.error || 'no se pudo iniciar'));
      }
    } catch (e) {
      setMensaje('❌ ' + e.message);
    } finally {
      setIniciando(false);
      setTimeout(() => setMensaje(null), 5000);
    }
  };

  const corriendo = !!estado?.corriendo;

  return (
    <div style={{ ...estiloTarjeta, marginTop: 16, padding: 16 }}>
      <div style={{ fontSize: 13, fontWeight: 700, color: COLOR_OSCURO, marginBottom: 6 }}>
        🌳 Árbol de categorías Syscom
      </div>
      <div style={{ fontSize: 12, color: COLOR_GRIS_CLARO, marginBottom: 12, lineHeight: 1.5 }}>
        Descarga la jerarquía completa (3 niveles) desde la API y la guarda en BD local.
        Permite filtrar productos por subcategoría con descendientes.
      </div>

      {arbolInfo && (
        <div style={{ fontSize: 12, color: COLOR_GRIS, marginBottom: 12 }}>
          <strong>{arbolInfo.total}</strong> categoría{arbolInfo.total === 1 ? '' : 's'} en BD ·
          <strong style={{ marginLeft: 4 }}>{arbolInfo.raices}</strong> raíces
        </div>
      )}

      {corriendo && (
        <div style={{
          padding: 12, background: '#fff8ed', borderRadius: 8,
          border: '1px solid #fce5b8', marginBottom: 12, fontSize: 12,
        }}>
          <div style={{ fontWeight: 700, color: '#a86b1e', marginBottom: 4 }}>
            ⏳ Sincronizando…
          </div>
          <div>Procesando: <strong>{estado.procesando || '—'}</strong></div>
          <div>Total guardadas hasta ahora: <strong>{estado.total}</strong></div>
          <div>
            N1: {estado.niveles?.[1] || 0} ·
            N2: {estado.niveles?.[2] || 0} ·
            N3: {estado.niveles?.[3] || 0}
          </div>
        </div>
      )}

      {!corriendo && estado?.terminado_en && estado?.total > 0 && (
        <div style={{
          padding: 12, background: '#e8faf5', borderRadius: 8,
          border: '1px solid #b8e8d4', marginBottom: 12, fontSize: 12,
        }}>
          <div style={{ fontWeight: 700, color: '#16a34a', marginBottom: 4 }}>
            ✅ Última sincronización
          </div>
          <div>{estado.total} categorías sincronizadas (N1: {estado.niveles?.[1]} · N2: {estado.niveles?.[2]} · N3: {estado.niveles?.[3]})</div>
          <div style={{ color: COLOR_GRIS_CLARO, fontSize: 11, marginTop: 4 }}>
            {new Date(estado.terminado_en).toLocaleString('es-MX')}
          </div>
        </div>
      )}

      <button
        style={{
          ...estiloBotonPrimario,
          opacity: iniciando || corriendo ? 0.6 : 1,
          cursor: iniciando || corriendo ? 'not-allowed' : 'pointer',
        }}
        disabled={iniciando || corriendo}
        onClick={iniciarSync}
      >
        {iniciando ? '⏳ Iniciando…' : corriendo ? '⏳ Sincronizando…' : '🌳 Sincronizar árbol de categorías'}
      </button>

      {mensaje && (
        <div style={{ marginTop: 10, fontSize: 12, color: COLOR_GRIS }}>{mensaje}</div>
      )}
    </div>
  );
}

// ============================================
// PanelActualizarCatalogo — REFRESCO-MASIVO-SYSCOM (2026-09-03)
// "Actualizar catalogo desde Syscom": TODO el catalogo (precios, existencias,
// titulo, imagen) o SOLO los productos en uso (ficha completa). Corre en segundo
// plano (se puede cerrar la pantalla), muestra progreso en vivo, resumen final,
// quien lo lanzo, historial y el estado del refresco AUTOMATICO.
// Endpoints: GET /api/syscom/refresco/estado (cualquier usuario con la pantalla;
// devuelve puede_lanzar calculado en el backend), POST /refresco/iniciar y
// POST /refresco/cancelar (solo direccion/administracion, validado en backend).
// ============================================
function fmtNum(n) {
  const v = Number(n);
  return Number.isFinite(v) ? v.toLocaleString('es-MX') : '—';
}

function PanelActualizarCatalogo() {
  const [data, setData] = useState(null);
  const [lanzando, setLanzando] = useState(null); // 'precios' | 'info' | 'cancelar'
  const [mensaje, setMensaje] = useState(null);
  const intervalRef = useRef(null);

  // Deny-by-default: el backend exige direccion/administracion (soloAdmin). Si
  // responde 403, el panel no se muestra (mismo criterio que PanelImportarCatalogo).
  const cargar = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE_CLI}/api/syscom/refresco/estado`, { headers: importAuthHeaders() });
      if (res.status === 403 || res.status === 401) { setData(null); return; }
      const r = await res.json();
      if (r.ok) setData(r);
    } catch {}
  }, []);

  useEffect(() => { cargar(); }, [cargar]);

  const precios = data?.precios || {};
  const info = data?.info || {};
  const desc = data?.descontinuados || {};
  const enCurso = precios.estado === 'corriendo' ? { tipo: 'precios', ...precios }
    : info.estado === 'corriendo' ? { tipo: 'info', ...info }
    : desc.estado === 'corriendo' ? { tipo: 'descontinuados', ...desc } : null;
  const corriendo = !!enCurso && !enCurso.stale;

  // Polling cada 4s mientras corre; se detiene en cuanto termina.
  useEffect(() => {
    if (corriendo) {
      if (intervalRef.current) return;
      intervalRef.current = setInterval(() => { cargar(); }, 4000);
    } else if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    return () => {
      if (intervalRef.current) { clearInterval(intervalRef.current); intervalRef.current = null; }
    };
  }, [corriendo, cargar]);

  const avisar = (txt, ms = 7000) => { setMensaje(txt); setTimeout(() => setMensaje(null), ms); };

  const lanzar = async (tipo) => {
    const texto = tipo === 'precios'
      ? `Se actualizarán precios, existencias, título e imagen de TODO el catálogo Syscom (~${fmtNum(data?.productos_catalogo)} productos) en segundo plano. Tarda unos 15-20 minutos; puedes cerrar la pantalla. Las cotizaciones ya guardadas NO cambian. ¿Continuar?`
      : `Se actualizará la ficha completa de los ${fmtNum(data?.productos_en_uso)} productos en uso (cotizaciones recientes, colecciones y listas pactadas). Tarda unos minutos. ¿Continuar?`;
    if (!window.confirm(texto)) return;
    setLanzando(tipo);
    try {
      const res = await fetch(`${API_BASE_CLI}/api/syscom/refresco/iniciar`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...importAuthHeaders() },
        body: JSON.stringify({ tipo }),
      });
      const r = await res.json().catch(() => ({}));
      if (res.status === 409) avisar('⚠️ Ya hay una actualización en curso.');
      else if (res.status === 401) avisar('🔒 Tu sesión expiró. Vuelve a iniciar sesión y reintenta.');
      else if (res.status === 403) avisar('🔒 Solo Dirección/Administración puede lanzar la actualización.');
      else if (res.ok && r.ok) avisar('✅ Actualización lanzada en segundo plano. Puedes cerrar esta pantalla.');
      else avisar('⚠️ ' + (r.error || 'No se pudo iniciar.'));
      if (r.precios) setData(r);
      cargar();
    } catch (e) {
      avisar('❌ ' + e.message);
    } finally {
      setLanzando(null);
    }
  };

  const cancelar = async () => {
    if (!enCurso) return;
    if (!window.confirm('¿Detener la actualización? Lo ya actualizado se conserva.')) return;
    setLanzando('cancelar');
    try {
      const res = await fetch(`${API_BASE_CLI}/api/syscom/refresco/cancelar`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...importAuthHeaders() },
        body: JSON.stringify({ tipo: enCurso.tipo }),
      });
      const r = await res.json().catch(() => ({}));
      avisar(res.ok && r.ok ? '⏹️ Se detendrá en la siguiente página.' : '⚠️ ' + (r.error || 'No se pudo cancelar.'));
      cargar();
    } catch (e) {
      avisar('❌ ' + e.message);
    } finally {
      setLanzando(null);
    }
  };

  if (!data) return null;

  const puedeLanzar = !!data.puede_lanzar;
  const hecho = Number(enCurso?.objetivo_hecho) || 0;
  const total = Number(enCurso?.objetivo_total) || 0;
  const pct = total > 0 ? Math.min(100, Math.round((hecho / total) * 100)) : 0;
  const etiquetaTipo = (t) => (t === 'precios' ? 'todo el catálogo' : t === 'descontinuados' ? 'verificación de descontinuados' : 'productos en uso');
  const etiquetaModo = (m) => (m === 'automatico' ? 'automático' : 'manual');
  const botonBase = { ...estiloBotonPrimario, minHeight: 44, fontSize: 14 };
  const botonSec = { ...estiloBotonSecundario, minHeight: 44, fontSize: 14 };
  const bloqueado = !!lanzando || corriendo;

  const ResumenCorrida = ({ fila, titulo }) => {
    if (!fila || !fila.ultima_completada_at) return null;
    return (
      <div style={{ fontSize: 12, color: COLOR_GRIS, lineHeight: 1.6 }}>
        <div>
          <strong>{titulo}:</strong> {fmtFecha(fila.ultima_completada_at)}
          {hace(fila.ultima_completada_at) ? ` (${hace(fila.ultima_completada_at)})` : ''}
          {fila.lanzado_por ? ` · por ${fila.lanzado_por}` : ''}
          {fila.modo ? ` · ${etiquetaModo(fila.modo)}` : ''}
        </div>
        {fila.estado === 'completo' && (
          <div style={{ color: COLOR_GRIS_CLARO }}>
            {fmtNum(fila.total_procesados)} revisados · <span style={{ color: '#16a34a', fontWeight: 600 }}>{fmtNum(fila.actualizados)} con cambios</span> · {fmtNum(fila.sin_cambio)} sin cambio
            {Number(fila.nuevos) > 0 ? ` · ${fmtNum(fila.nuevos)} nuevos` : ''}
            {Number(fila.descontinuados) > 0 ? ` · ${fmtNum(fila.descontinuados)} descontinuados a existencia 0` : ''}
            {Number(fila.errores) > 0 ? <span style={{ color: '#dc2626' }}> · {fmtNum(fila.errores)} con error</span> : ' · 0 errores'}
          </div>
        )}
      </div>
    );
  };

  return (
    <div style={{ ...estiloTarjeta, marginTop: 16, padding: 16 }}>
      <div style={{ fontSize: 13, fontWeight: 700, color: COLOR_OSCURO, marginBottom: 6 }}>
        🔄 Actualizar catálogo desde Syscom
      </div>
      <div style={{ fontSize: 12, color: COLOR_GRIS_CLARO, marginBottom: 12, lineHeight: 1.5 }}>
        Trae de Syscom los precios, existencias, títulos e imágenes actuales de los {fmtNum(data.productos_catalogo)} productos
        del catálogo, en segundo plano (puedes cerrar la pantalla). Las cotizaciones ya guardadas conservan sus precios congelados.
      </div>

      {/* Ultimas actualizaciones completas */}
      <div style={{ display: 'grid', gap: 4, marginBottom: 12 }}>
        <ResumenCorrida fila={precios} titulo="Última actualización de todo el catálogo" />
        <ResumenCorrida fila={info} titulo="Última actualización de productos en uso" />
        {!precios.ultima_completada_at && !info.ultima_completada_at && (
          <div style={{ fontSize: 12, color: COLOR_GRIS_CLARO }}>Todavía no se ha corrido ninguna actualización masiva.</div>
        )}
        {/* DESCONTINUADOS: lo que Syscom deja de listar se verifica producto por producto contra su
            ficha; solo lo que ya no existe pasa a existencia 0 con etiqueta. */}
        <div style={{ fontSize: 12, color: COLOR_GRIS, lineHeight: 1.6 }}>
          <strong>Descontinuados por proveedor:</strong> {fmtNum(data.etiquetados)} productos en existencia 0 con etiqueta
          {Number(desc.candidatos) > 0 ? ` · ${fmtNum(desc.candidatos)} pendientes de verificar contra la ficha de Syscom` : ' · nada pendiente de verificar'}
          {desc.ultima_completada_at ? ` · última verificación ${fmtFecha(desc.ultima_completada_at)} (${fmtNum(desc.actualizados)} seguían a la venta, ${fmtNum(desc.descontinuados)} descontinuados)` : ''}
        </div>
      </div>

      {/* Progreso en vivo */}
      {enCurso && !enCurso.stale && (
        <div style={{ padding: 12, background: '#fff8ed', borderRadius: 8, border: '1px solid #fce5b8', marginBottom: 12, fontSize: 12 }}>
          <div style={{ fontWeight: 700, color: '#a86b1e', marginBottom: 6 }}>
            ⏳ Actualizando {etiquetaTipo(enCurso.tipo)}… {total > 0 ? `${pct}%` : ''}
          </div>
          <div style={{ height: 10, background: '#fde7c2', borderRadius: 6, overflow: 'hidden', marginBottom: 8 }}>
            <div style={{ width: `${pct}%`, height: '100%', background: '#f59e0b', transition: 'width .4s' }} />
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px 14px' }}>
            <span>
              {enCurso.tipo === 'precios'
                ? <>Página <strong>{fmtNum(hecho)}</strong> de <strong>{fmtNum(total)}</strong></>
                : <>Producto <strong>{fmtNum(hecho)}</strong> de <strong>{fmtNum(total)}</strong></>}
            </span>
            <span>Revisados: <strong>{fmtNum(enCurso.total_procesados)}</strong></span>
            {enCurso.tipo === 'descontinuados' ? (
              <>
                <span>Siguen a la venta: <strong>{fmtNum(enCurso.actualizados)}</strong></span>
                <span>Descontinuados: <strong>{fmtNum(enCurso.descontinuados)}</strong></span>
              </>
            ) : (
              <>
                <span>Con cambios: <strong>{fmtNum(enCurso.actualizados)}</strong></span>
                <span>Sin cambio: <strong>{fmtNum(enCurso.sin_cambio)}</strong></span>
              </>
            )}
            {Number(enCurso.errores) > 0 && <span style={{ color: '#dc2626' }}>Errores: <strong>{fmtNum(enCurso.errores)}</strong></span>}
          </div>
          <div style={{ color: COLOR_GRIS_CLARO, fontSize: 11, marginTop: 6 }}>
            Lanzada {etiquetaModo(enCurso.modo)}{enCurso.lanzado_por ? ` por ${enCurso.lanzado_por}` : ''}
            {enCurso.ultima_corrida_at ? ` · ${fmtFecha(enCurso.ultima_corrida_at)}` : ''}
            {enCurso.cancelar ? ' · deteniendo…' : ''}
          </div>
        </div>
      )}

      {enCurso && enCurso.stale && (
        <div style={{ padding: 12, background: '#fff1e6', borderRadius: 8, border: '1px solid #f5c6a0', marginBottom: 12, fontSize: 12 }}>
          <div style={{ fontWeight: 700, color: '#c2410c', marginBottom: 4 }}>⚠️ La actualización de {etiquetaTipo(enCurso.tipo)} quedó a medias</div>
          <div style={{ color: COLOR_GRIS }}>
            Seguramente el servidor se reinició. Se retoma sola al arrancar; si no, vuelve a lanzarla y continuará donde quedó ({fmtNum(hecho)} de {fmtNum(total)}).
          </div>
        </div>
      )}

      {[precios, info].map((fila, idx) => fila.estado === 'error' && (
        <div key={idx} style={{ padding: 12, background: '#fde8e8', borderRadius: 8, border: '1px solid #f5b8b8', marginBottom: 12, fontSize: 12 }}>
          <div style={{ fontWeight: 700, color: '#dc2626', marginBottom: 4 }}>❌ La actualización de {etiquetaTipo(idx === 0 ? 'precios' : 'info')} se detuvo con error</div>
          <div>{fila.mensaje_error || 'Error desconocido'}</div>
          <div style={{ color: COLOR_GRIS_CLARO, fontSize: 11, marginTop: 4 }}>Puedes volver a lanzarla: retomará donde quedó.</div>
        </div>
      ))}

      {[precios, info].map((fila, idx) => fila.estado === 'cancelado' && (
        <div key={'c' + idx} style={{ fontSize: 12, color: COLOR_GRIS, marginBottom: 10 }}>
          ⏹️ La última actualización de {etiquetaTipo(idx === 0 ? 'precios' : 'info')} se detuvo a petición ({fmtNum(fila.total_procesados)} revisados). Lo actualizado se conservó.
        </div>
      ))}

      {/* Botones (solo direccion/administracion; el backend lo valida tambien) */}
      {puedeLanzar && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center' }}>
          <button
            style={{ ...botonBase, opacity: bloqueado ? 0.6 : 1, cursor: bloqueado ? 'not-allowed' : 'pointer' }}
            disabled={bloqueado}
            onClick={() => lanzar('precios')}
          >
            {lanzando === 'precios' ? '⏳ Iniciando…' : corriendo ? '⏳ Actualizando…' : '🔄 Actualizar todo desde Syscom'}
          </button>
          <button
            style={{ ...botonSec, opacity: bloqueado ? 0.6 : 1, cursor: bloqueado ? 'not-allowed' : 'pointer' }}
            disabled={bloqueado}
            onClick={() => lanzar('info')}
          >
            {lanzando === 'info' ? '⏳ Iniciando…' : `Solo productos en uso (${fmtNum(data.productos_en_uso)})`}
          </button>
          {corriendo && (
            <button
              style={{ ...botonSec, color: '#dc2626', opacity: lanzando ? 0.6 : 1, cursor: lanzando ? 'not-allowed' : 'pointer' }}
              disabled={!!lanzando}
              onClick={cancelar}
            >
              {lanzando === 'cancelar' ? '⏳…' : '⏹️ Detener'}
            </button>
          )}
        </div>
      )}
      {!puedeLanzar && (
        <div style={{ fontSize: 12, color: COLOR_GRIS_CLARO }}>Solo Dirección/Administración puede lanzar la actualización.</div>
      )}

      {mensaje && <div style={{ marginTop: 10, fontSize: 12, color: COLOR_GRIS }}>{mensaje}</div>}

      {/* Automatico */}
      <div style={{ marginTop: 14, padding: 12, background: '#eef6ff', borderRadius: 8, border: '1px solid #cfe3ff', fontSize: 12, lineHeight: 1.6 }}>
        <div style={{ fontWeight: 700, color: '#1d4ed8', marginBottom: 2 }}>🤖 Refresco automático {data.automatico?.activo ? 'activo' : 'apagado'}</div>
        <div style={{ color: COLOR_GRIS }}>
          Todo el catálogo cada <strong>{precios.cadencia_dias}</strong> días
          {precios.proximo_at ? ` · próximo: ${fmtFecha(precios.proximo_at)}` : ' · próximo: en la siguiente revisión'}
          {' '}· Productos en uso cada <strong>{info.cadencia_dias}</strong> días
          {info.proximo_at ? ` · próximo: ${fmtFecha(info.proximo_at)}` : ' · próximo: en la siguiente revisión'}
        </div>
        <div style={{ color: COLOR_GRIS_CLARO, fontSize: 11 }}>
          El servidor revisa cada {data.automatico?.tick_minutos} min si toca actualizar
          {data.automatico?.ultimo_tick_at ? ` · última revisión ${hace(data.automatico.ultimo_tick_at)}` : ' · aún sin revisión desde el último arranque'}.
        </div>
      </div>

      {/* Historial */}
      {Array.isArray(data.historial) && data.historial.length > 0 && (
        <div style={{ marginTop: 14 }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: COLOR_OSCURO, marginBottom: 6 }}>Historial reciente</div>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12, minWidth: 520 }}>
              <thead>
                <tr style={{ color: COLOR_GRIS_CLARO, textAlign: 'left' }}>
                  <th style={{ padding: '4px 6px', fontWeight: 600 }}>Cuándo</th>
                  <th style={{ padding: '4px 6px', fontWeight: 600 }}>Qué</th>
                  <th style={{ padding: '4px 6px', fontWeight: 600 }}>Quién</th>
                  <th style={{ padding: '4px 6px', fontWeight: 600 }}>Resultado</th>
                </tr>
              </thead>
              <tbody>
                {data.historial.map(h => (
                  <tr key={h.id} style={{ borderTop: `1px solid ${COLOR_BORDE}` }}>
                    <td style={{ padding: '6px 6px', whiteSpace: 'nowrap' }}>{fmtFecha(h.iniciado_at)}</td>
                    <td style={{ padding: '6px 6px' }}>{etiquetaTipo(h.tipo)} · {etiquetaModo(h.modo)}</td>
                    <td style={{ padding: '6px 6px' }}>{h.lanzado_por || '—'}</td>
                    <td style={{ padding: '6px 6px' }}>
                      {h.estado === 'completo' ? '✅' : h.estado === 'corriendo' ? '⏳' : h.estado === 'cancelado' ? '⏹️' : '❌'}{' '}
                      {fmtNum(h.total_procesados)} revisados · {fmtNum(h.actualizados)} con cambios
                      {Number(h.descontinuados) > 0 ? ` · ${fmtNum(h.descontinuados)} descontinuados` : ''}
                      {Number(h.errores) > 0 ? ` · ${fmtNum(h.errores)} errores` : ''}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

// ============================================
// PanelImportarCatalogo — disparador + progreso de la carga inicial COMPLETA
// del catalogo Syscom (FASE B). Solo direccion/administracion. Endpoints
// protegidos: POST /api/syscom/import/iniciar y GET /api/syscom/import/estado.
// ============================================
function PanelImportarCatalogo() {
  // Gate alineado EXACTO con el backend (soloAdmin = rol 'direccion' O
  // 'administracion'). Antes dependia de accesoTotal (que solo cubre direccion),
  // una inconsistencia con el guard del backend. Esto es SOLO visibilidad del
  // boton; el backend sigue validando con soloAdmin, asi que no debilita nada.
  const { usuario } = useUsuario();
  const rolUsuario = String(usuario?.rol || '').toLowerCase();
  const autorizado = rolUsuario === 'direccion' || rolUsuario === 'dirección' || rolUsuario === 'administracion';

  const [estado, setEstado] = useState(null);
  const [catMap, setCatMap] = useState({});
  const [iniciando, setIniciando] = useState(false);
  const [mensaje, setMensaje] = useState(null);
  const intervalRef = useRef(null);

  const cargarEstado = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE_CLI}/api/syscom/import/estado`, { headers: importAuthHeaders() });
      const r = await res.json();
      if (r.ok) setEstado(r.estado);
    } catch {}
  }, []);

  // Mapa id -> nombre de categorias para mostrar el nombre legible (no el id).
  const cargarCategorias = useCallback(async () => {
    try {
      const r = await fetch(`${API_BASE_CLI}/api/syscom/categorias-arbol`).then(x => x.json());
      if (!r.ok) return;
      const map = {};
      const recorrer = (nodos) => {
        for (const n of (nodos || [])) {
          map[String(n.id)] = n.nombre;
          if (n.children) recorrer(n.children);
        }
      };
      recorrer(r.arbol || []);
      setCatMap(map);
    } catch {}
  }, []);

  useEffect(() => {
    if (!autorizado) return;
    cargarEstado();
    cargarCategorias();
  }, [autorizado, cargarEstado, cargarCategorias]);

  const corriendo = estado?.estado === 'corriendo';

  // Polling cada 4s mientras corre; se detiene en cuanto termina.
  useEffect(() => {
    if (corriendo) {
      if (intervalRef.current) return;
      intervalRef.current = setInterval(() => { cargarEstado(); }, 4000);
    } else if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    return () => {
      if (intervalRef.current) { clearInterval(intervalRef.current); intervalRef.current = null; }
    };
  }, [corriendo, cargarEstado]);

  const iniciarImport = async () => {
    if (!window.confirm('Esto descargara el catalogo COMPLETO de Syscom (~50,000 productos) en segundo plano. Tarda unos 15-20 minutos y es reanudable. ¿Continuar?')) return;
    setIniciando(true);
    setMensaje(null);
    try {
      const res = await fetch(`${API_BASE_CLI}/api/syscom/import/iniciar`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...importAuthHeaders() },
      });
      const r = await res.json().catch(() => ({}));
      if (res.status === 409) {
        setMensaje('⚠️ Ya hay una importación en curso.');
        if (r.estado) setEstado(r.estado);
      } else if (res.status === 401) {
        setMensaje('🔒 Tu sesión expiró o no es válida. Cierra sesión y vuelve a iniciar sesión, luego reintenta.');
      } else if (res.status === 403) {
        setMensaje('🔒 No tienes permiso para lanzar la importación (solo Dirección/Administración).');
      } else if (res.ok && r.ok) {
        setMensaje('✅ Importación lanzada en segundo plano — verás el progreso aquí.');
        if (r.estado) setEstado(r.estado);
        cargarEstado();
      } else {
        setMensaje('⚠️ ' + (r.error || 'no se pudo iniciar'));
      }
    } catch (e) {
      setMensaje('❌ ' + e.message);
    } finally {
      setIniciando(false);
      setTimeout(() => setMensaje(null), 6000);
    }
  };

  // Deny-by-default: solo direccion/admin ve este panel.
  if (!autorizado) return null;

  const nombreCat = estado?.categoria_actual
    ? (catMap[String(estado.categoria_actual)] || `#${estado.categoria_actual}`)
    : null;
  const esError = estado?.estado === 'error';
  const esCompleto = estado?.estado === 'completo';
  // Categorias que el proveedor no dejo leer en la ultima corrida (mig_089).
  const bloqueadas = Array.isArray(estado?.categorias_bloqueadas) ? estado.categorias_bloqueadas : [];
  const saltoCategorias = bloqueadas.length > 0;
  // Atasco: 'corriendo' pero sin heartbeat reciente (proceso muerto en un
  // reinicio o cuelgue). Umbral igual al del backend (STALE_SEGUNDOS = 360s).
  const UMBRAL_ATASCO_SEG = 360;
  const atascada = corriendo && Number(estado?.segundos_sin_avanzar) > UMBRAL_ATASCO_SEG;
  const minSinAvanzar = atascada ? Math.max(1, Math.floor(Number(estado.segundos_sin_avanzar) / 60)) : 0;
  const bloqueado = iniciando || (corriendo && !atascada);
  const labelBoton = iniciando ? '⏳ Iniciando…'
    : (corriendo && !atascada) ? '⏳ Importando…'
    : atascada ? '🔄 Reanudar importación'
    : '📦 Importar catálogo completo de Syscom';

  return (
    <div style={{ ...estiloTarjeta, marginTop: 16, padding: 16 }}>
      <div style={{ fontSize: 13, fontWeight: 700, color: COLOR_OSCURO, marginBottom: 6 }}>
        📦 Importar catálogo completo de Syscom
      </div>
      <div style={{ fontSize: 12, color: COLOR_GRIS_CLARO, marginBottom: 12, lineHeight: 1.5 }}>
        Descarga TODO el catálogo (~50,000 productos) a la base local, en segundo plano.
        Tarda ~15-20 minutos y es reanudable: si se corta, continúa donde quedó al volver a lanzarla.
      </div>

      {corriendo && !atascada && (
        <div style={{ padding: 12, background: '#fff8ed', borderRadius: 8, border: '1px solid #fce5b8', marginBottom: 12, fontSize: 12 }}>
          <div style={{ fontWeight: 700, color: '#a86b1e', marginBottom: 4 }}>⏳ Importando…</div>
          <div>Categoría actual: <strong>{nombreCat || '—'}</strong></div>
          <div>Página: <strong>{estado.pagina_actual ?? '—'}</strong></div>
          <div>Productos procesados: <strong>{estado.total_procesados ?? 0}</strong></div>
        </div>
      )}

      {atascada && (
        <div style={{ padding: 12, background: '#fff1e6', borderRadius: 8, border: '1px solid #f5c6a0', marginBottom: 12, fontSize: 12 }}>
          <div style={{ fontWeight: 700, color: '#c2410c', marginBottom: 4 }}>⚠️ La importación parece atascada</div>
          <div>
            Sin avanzar desde hace <strong>{minSinAvanzar} min</strong> (categoría <strong>{nombreCat || '—'}</strong>,
            página <strong>{estado.pagina_actual ?? '—'}</strong>, <strong>{estado.total_procesados ?? 0}</strong> productos).
          </div>
          <div style={{ color: COLOR_GRIS_CLARO, fontSize: 11, marginTop: 4 }}>
            Seguramente el servidor se reinició. Pulsa <strong>Reanudar importación</strong> para que continúe desde
            donde quedó (no se pierde lo ya cargado).
          </div>
        </div>
      )}

      {esCompleto && (
        // SYSCOM-REIMPORT (2026-09-21): "completa" ya no es automaticamente verde. Si el
        // barrido tuvo que saltarse categorias que el proveedor no dejo leer, la tarjeta se
        // pone ambar y las NOMBRA. Un hueco del catalogo escondido detras de una palomita
        // verde es justo como se perdieron 24 dias.
        <div style={{
          padding: 12, borderRadius: 8, marginBottom: 12, fontSize: 12,
          background: saltoCategorias ? '#fff8ed' : '#e8faf5',
          border: `1px solid ${saltoCategorias ? '#fce5b8' : '#b8e8d4'}`,
        }}>
          <div style={{ fontWeight: 700, color: saltoCategorias ? '#b45309' : '#16a34a', marginBottom: 4 }}>
            {saltoCategorias
              ? `⚠️ Última importación completa, pero se saltó ${bloqueadas.length === 1 ? '1 categoría' : bloqueadas.length + ' categorías'}`
              : '✅ Última importación completa'}
          </div>
          <div><strong>{estado.total_procesados}</strong> productos procesados</div>
          {saltoCategorias && (
            <ul style={{ margin: '6px 0 0', paddingLeft: 18, color: '#8a5a1b' }}>
              {bloqueadas.map((b, i) => (
                <li key={i} style={{ marginBottom: 2 }}>
                  <strong>{b.nombre || 'Categoría'} ({b.id})</strong>: {b.motivo}
                </li>
              ))}
            </ul>
          )}
          {estado.ultima_completada_at && (
            <div style={{ color: COLOR_GRIS_CLARO, fontSize: 11, marginTop: 4 }}>
              {new Date(estado.ultima_completada_at).toLocaleString('es-MX')}
            </div>
          )}
        </div>
      )}

      {esError && (
        <div style={{ padding: 12, background: '#fde8e8', borderRadius: 8, border: '1px solid #f5b8b8', marginBottom: 12, fontSize: 12 }}>
          <div style={{ fontWeight: 700, color: '#dc2626', marginBottom: 4 }}>❌ La última importación falló</div>
          <div>{estado.mensaje_error || 'Error desconocido'}</div>
          <div style={{ color: COLOR_GRIS_CLARO, fontSize: 11, marginTop: 4 }}>
            Puedes volver a lanzarla: retomará donde quedó.
          </div>
        </div>
      )}

      <button
        style={{
          ...estiloBotonPrimario,
          opacity: bloqueado ? 0.6 : 1,
          cursor: bloqueado ? 'not-allowed' : 'pointer',
        }}
        disabled={bloqueado}
        onClick={iniciarImport}
      >
        {labelBoton}
      </button>

      {mensaje && (
        <div style={{ marginTop: 10, fontSize: 12, color: COLOR_GRIS }}>{mensaje}</div>
      )}
    </div>
  );
}
